// The COMP processor for the channel tuning screen.
//
// CURVE is the compressor's input/output response — the picture the unit's own
// COMP screen draws (user guide p.104), with the live level travelling it. The
// unit lets you drag T / R / G grips on that graph; this screen deliberately does
// not. Three grips on one plot means a press has to guess which value was meant,
// and one that missed fell through to the threshold, so pressing the gain grip
// moved the threshold. The sliders beside the plot are the editing path, and the
// curve answers "what is this doing to my signal". The lane rack beside it is the same
// three-tap ruler the gate screen uses.
//
// Two device facts shape it, both measured on a URX44V (2026-07-29):
//   - COMP GR (110) reports the reduction alone. Sweeping the makeup gain 0 →
//     +18 dB moved the downstream tap by exactly 18 dB and left the GR meter where
//     it was, so the lane stays readable at any makeup setting;
//   - a compressor's reduction occupies a few dB of the 54 dB input ruler, so the
//     GR lane carries a scale of its own rather than the level lanes' dB per pixel
//     (a -8 dB reduction is 15% of the shared ruler — visible, but not readable).

import { onOff, settingsChoice, settingsRow, settingsSection } from "./dom";
import type { SettingsRowOptions } from "./dom";
import {
  COMP_EQ_COMP_FIRST,
  COMP_KNEE_DEFAULT,
  COMP_KNEE_OPTIONS,
  compDeviceDriven,
  compOneKnobPatch,
} from "../core/control/params";
import { channelDynamics } from "../core/control/translate";
import { COMP_SCOPE, controlId } from "../core/midi/controls";
import type { ControlParam } from "../core/midi/controls";
import { bindChannelStrip, pairOwnNodes, subObjectIo } from "./dyn-chan";
import { drawTransferCurve, kneeResponse, transferPlot } from "./dyn-plot";
import { flagOffNote, oneKnobLevelRow } from "./dyn-screen";
import type { DynCtx, DynPlotProcessor, DynValues } from "./dyn-screen";

/** Input axis = the threshold's own domain (-54…0 dB). Ticks every 6 dB. */
const LO_DB = -54;

// Output axis. Unlike the gate's, the compressor's output runs ABOVE the input
// range: makeup gain reaches +18 dB, and an output clamped at 0 would hide every
// makeup setting past the point where the curve reaches the ceiling.
const OUT_LO_DB = -54;
const OUT_TICKS = [18, 6, -6, -18, -30, -42, -54];

/**
 * Knee width in dB by selector value (0 Soft / 1 Medium / 2 Hard), from the
 * threshold-departure measurement in reference/work/vd/vd-params.md: with the
 * signal held still, the threshold was walked upward until the reduction stopped,
 * which is where the knee's lower edge leaves the detector behind. Hard stopped
 * immediately, Medium ~8 dB later, Soft ~20 dB later — so these are twice the
 * measured reach under the usual symmetric-knee model.
 *
 * All three have since been measured the other way round — the threshold held and the
 * SIGNAL walked past it in 1 dB steps, which is the direction the curve is read in —
 * and fitted together against ONE detector-versus-meter offset (1.10 dB, 113 points),
 * since that offset belongs to the rig rather than to the knee selector. Medium and Hard
 * came back as they were. SOFT DID NOT: its residual minimum is 51 dB against the 40 that
 * doubling gave, which fits 62% worse, so the constant carries the measurement.
 *
 * The doubling worked for Medium and failed for Soft because the knee is not symmetric:
 * a 20 dB lower reach against a 51 dB total leaves about 31 dB above the threshold. What
 * a symmetric model can be fitted to is therefore the best symmetric APPROXIMATION, which
 * is exactly what this constant has to hold — 50, 52 and 54 dB are indistinguishable
 * (0.275 / 0.274 / 0.283), so it takes the round middle.
 *
 * The symmetry itself is a per-block property, not a house rule. The SSMCS strip's
 * compressor — the other bank on the same channel — has an asymmetric knee, which a
 * single width per setting cannot express; its reaches are a pair, in dyn-ssmcs.ts. So
 * neither bank's constants may be carried across to the other.
 *
 * What stays an assumption here is the curvature BETWEEN the edges: the standard
 * quadratic interpolation. The signal-side fit above cannot separate it from nearby
 * shapes at 1 dB of meter quantization.
 */
const KNEE_WIDTH_DB = [52, 16, 0];

const kneeWidth = (knee: number): number => KNEE_WIDTH_DB[knee] ?? KNEE_WIDTH_DB[COMP_KNEE_DEFAULT];

/** The compressor's response as a function of input level, makeup included. Built
 *  once per redraw rather than read per sample point: the curve evaluates it ~120
 *  times, and each `v.get` walks the plan. */
function responseOf(v: DynValues): (inDb: number) => number {
  // Half the width each side: this block's knee was fitted as one symmetric number, and
  // `kneeResponse` reproduces exactly what the quadratic above it used to draw.
  const w = kneeWidth(Math.round(v.get("knee")));
  return kneeResponse({
    thr: v.get("threshold"),
    ratio: v.get("ratio"),
    up: w / 2,
    down: w / 2,
    gain: v.get("gain"),
  });
}

const io = subObjectIo("comp");

/** The makeup this curve carries, for the axes layer — which is handed the context rather
 *  than the values, and so cannot ask `responseOf`. The default comes off the field table
 *  rather than a literal, so the two cannot drift. */
function makeupOf(ctx: DynCtx): number {
  const dyn = channelDynamics(ctx.model, ctx.nodeId, ctx.plan.nodeParams[ctx.nodeId]?.compEqType ?? COMP_EQ_COMP_FIRST);
  const def = dyn?.comp?.find((f) => f.key === "gain")?.def ?? 0;
  const v = (ctx.plan.nodeParams[ctx.nodeId]?.comp as Record<string, unknown> | undefined)?.gain;
  return typeof v === "number" ? v : def;
}

export const COMP_DYN: DynPlotProcessor = {
  // A linked pair's screen reads its primary's Signal Type and both members' meter
  // addresses, so the pair is what this screen draws rather than the node it was opened on.
  ownNodes: pairOwnNodes,
  key: "comp",
  loDb: LO_DB,
  tickStep: 6,
  title: (m) => m.dynTuning.comp.title,
  bind: (ctx) =>
    bindChannelStrip(ctx, {
      fields: (dyn) => dyn.comp,
      grKind: "comp",
      inTapKey: "precomp",
      outTapKey: "preeq",
      cap: "threshold",
      // The makeup reaches +18 dB here, and the overlap is `108` + makeup in dBFS — so a
      // hot input runs the reduction into the level exactly as the SSMCS strip's does,
      // just from a higher input. Merged means relative: the rule is one rule.
      grNetDb: makeupOf(ctx),
    }),
  // No Display bar: see dyn-gate.ts.
  ...transferPlot({
    loDb: LO_DB,
    outLoDb: OUT_LO_DB,
    outTicks: OUT_TICKS,
    hint: (m) => m.dynTuning.comp.curveHint,
    unityOffsetDb: (ctx) => makeupOf(ctx),
  }),
  offNote: (ctx) => flagOffNote(ctx, "compOn"),
  // A comp group holding no knee reads as the default knee, which is the one the Knee row
  // shows: knee is not a field, so the host's own fallback for it is 0 (Soft). Read-side
  // only — `patch` merges onto what the plan holds.
  read: (ctx) => ({ knee: COMP_KNEE_DEFAULT, ...io.read(ctx) }),
  patch: io.patch,
  // Every key the edit carries, since the 1-knob switch writes attack, release, Auto Makeup and
  // the level at once, and any of them can already hold the value it writes.
  written: (_ctx, patch) => Object.keys(patch).map((key) => `comp.${key}`),
  // Knee is a three-value selector, which the catalog does not carry: a control
  // that answers null neither rings nor arms.
  controlId: (ctx, key) => (key === "knee" ? null : controlId(ctx.nodeId, key as ControlParam, COMP_SCOPE)),

  // The device drives threshold / ratio / gain / knee / attack / release while 1-knob is on
  // and the gain while Auto Makeup is on, and announces each recomputation per address — so
  // those rows keep updating on screen, read-only, rather than being hidden or recomputed here.
  //
  // 1-knob also decides which of the two rows around it applies: Auto Makeup cannot be
  // operated while it is on (user guide), and the level does nothing while it is off. That
  // is a lock, declared here with the rest, and not a swap in `rows` — a swap would break
  // the host's "no row is ever removed" invariant, and it did: a toggle row and a slider
  // row are not the same height, so the panel moved, and dropping Auto Makeup lifted the
  // 1-knob row a full row up, out from under the pointer that had just clicked it.
  rowStates: (ctx, vals) => {
    const out = new Map<string, SettingsRowOptions>();
    const one = vals.oneKnob === true;
    // The set the writer stops emitting, read from there rather than spelled again: a row
    // tagged "driven" while the plan still sends it is the drift.
    for (const k of compDeviceDriven(vals)) out.set(k, { tag: ctx.m.dynTuning.driven, locked: true });
    out.set(one ? "autoMakeup" : "oneKnobLevel", { locked: true });
    return out;
  },

  // The 1-knob is a stage of its own above the parameters, as it is on the EQ screen and
  // on the multi-band compressor's: it decides whose the rows below are. The unit prints
  // it that way too — [1-knob] and [Auto Makeup] are buttons of the COMP screen rather
  // than entries in its parameter list, in that order (p.104-105) — and Auto Makeup comes
  // with it, because the two lock each other (1-knob on takes Auto Makeup away; 1-knob off
  // takes the level away) and a lock is unreadable when its other half is in another
  // section.
  sections: ({ m, vals, states, set, setValue, midi }) => {
    const sec = settingsSection(m.inspector.oneKnob);
    sec.append(
      midi(
        settingsRow(
          m.inspector.on,
          onOff(vals.oneKnob === true, (on) => set(compOneKnobPatch(vals, on))),
        ),
        "oneKnob",
      ),
      midi(
        settingsRow(
          m.inspector.autoMakeup,
          onOff(vals.autoMakeup === true, (on) => set({ autoMakeup: on })),
          states.get("autoMakeup"),
        ),
        "autoMakeup",
      ),
      midi(
        oneKnobLevelRow({
          label: m.inspector.oneKnobLevel,
          value: vals.oneKnobLevel,
          onInput: (v) => setValue({ oneKnobLevel: v }),
          row: states.get("oneKnobLevel"),
        }),
        "oneKnobLevel",
      ),
    );
    return [sec];
  },

  // Knee goes in front of Attack, which is where the unit's own COMP screen puts it. The
  // parameters then read in the order the unit reads them, which is the rule every screen
  // here follows; a selector is not a class with a position of its own.
  rows: ({ m, vals, states, set }) => {
    const knee = typeof vals.knee === "number" ? vals.knee : COMP_KNEE_DEFAULT;
    return {
      before: {
        attack: [
          settingsRow(
            m.inspector.dyn.knee,
            settingsChoice(
              COMP_KNEE_OPTIONS.map((o) => o.label),
              knee,
              (i) => set({ knee: COMP_KNEE_OPTIONS[i].value }),
            ),
            states.get("knee"),
          ),
        ],
      },
    };
  },

  // The curve and its reduction annotation are the drawing both compressor banks make, so
  // both call one function; what differs is only the response each of them models. The
  // threshold is marked on the input axis for the reason `curveMarks` states: it is a kink
  // in the line and nothing else, so reading it off the plot otherwise means finding where
  // the slope changes and estimating it.
  drawCurve: (c, g, v, tok) => {
    drawTransferCurve(c, g, tok, {
      out: responseOf(v),
      gainDb: v.get("gain"),
      loDb: LO_DB,
      markAt: v.get("threshold"),
    });
  },
};
